/**
 * BookMatrixWidget — Grid of bookmakers × sports with live record counts.
 * Derived directly from live_odds so it does not depend on legacy RPC functions.
 * Prefix: bmw-
 */
import { useState, useEffect, useCallback, useMemo } from 'react'
import { fetchReceiverOdds } from '../lib/api'
import { getBookLogo } from '../lib/logoMaps'
import { normalizeLeagueCode } from '../lib/oddsNormalization'
import { RefreshCw, Grid3X3 } from 'lucide-react'
import './BookMatrixWidget.css'

const LEAGUE_ORDER = ['NFL', 'NBA', 'MLB', 'NHL', 'CFB', 'CBB', 'NCAAH', 'NLL', 'PWHL', 'AHL']
const MATRIX_PAST_WINDOW_HOURS = 12
const MATRIX_FUTURE_WINDOW_HOURS = 96
const MATRIX_FETCH_PLAN = [
  { sport: 'MLB', limit: 6000 },
  { sport: 'NBA', limit: 4000 },
  { sport: 'NHL', limit: 4000 },
  { sport: 'NCAAB', limit: 2500 },
  { sport: 'SOCCER', limit: 1500 },
  { sport: 'NFL', limit: 500 },
  { sport: 'CFB', limit: 500 },
  { sport: 'NCAAW', limit: 500 },
  { sport: 'MMA', limit: 500 },
  { sport: 'TENNIS', limit: 500 },
]

const LEAGUE_COLORS = {
  NFL: '#013369',
  NBA: '#C9082A',
  MLB: '#002D72',
  NHL: '#000',
  CFB: '#7C2D12',
  CBB: '#9C2608',
}

export default function BookMatrixWidget() {
  const [matrixData, setMatrixData] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [viewMode, setViewMode] = useState('odds') // 'odds' or 'games'

  const fetchMatrix = useCallback(async () => {
    setLoading(true)
    try {
      const grouped = new Map()
      const seenOdds = new Set()
      const receiverResults = await Promise.allSettled(
        MATRIX_FETCH_PLAN.map(({ sport, limit }) =>
          fetchReceiverOdds({ sport, limit: String(limit) })
        )
      )

      const successfulPayloads = receiverResults
        .map((result) => (result.status === 'fulfilled' ? result.value : null))
        .filter((result) => result?.status === 'ok')

      if (successfulPayloads.length === 0) {
        const firstFailure = receiverResults.find((result) =>
          result.status === 'fulfilled'
            ? result.value?.status && result.value.status !== 'ok'
            : true
        )
        if (firstFailure?.status === 'fulfilled') {
          throw new Error(firstFailure.value?.message || 'Receiver unavailable')
        }
        throw new Error('Receiver unavailable')
      }

      const now = Date.now()
      const minStart = now - MATRIX_PAST_WINDOW_HOURS * 3600000
      const maxStart = now + MATRIX_FUTURE_WINDOW_HOURS * 3600000

      successfulPayloads.forEach((payload) => {
        ;(payload.data?.odds || []).forEach((row) => {
          const rowKey = row.compound_key || `${row.bookmaker}|${row.canonical_id}|${row.market_normalized}|${row.selection}|${row.handicap ?? ''}`
          if (seenOdds.has(rowKey)) return
          seenOdds.add(rowKey)

          const startMs = row.start_time ? new Date(row.start_time).getTime() : Number.NaN
          if (!Number.isFinite(startMs) || startMs < minStart || startMs > maxStart) return
          const league = normalizeLeagueCode(row.league)
          if (!row.bookmaker || !league) return
          const key = `${row.bookmaker}|${league}`
          if (!grouped.has(key)) {
            grouped.set(key, {
              bookmaker: row.bookmaker,
              league,
              odds_count: 0,
              canonicalIds: new Set(),
            })
          }
          const entry = grouped.get(key)
          entry.odds_count += 1
          if (row.canonical_id) entry.canonicalIds.add(row.canonical_id)
        })
      })

      setMatrixData(Array.from(grouped.values()).map((entry) => ({
        bookmaker: entry.bookmaker,
        league: entry.league,
        odds_count: entry.odds_count,
        game_count: entry.canonicalIds.size,
      })))
      setError(null)
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchMatrix()
    const interval = setInterval(fetchMatrix, 60000)
    return () => clearInterval(interval)
  }, [fetchMatrix])

  // Build matrix structure
  const { bookmakers, leagues, matrix, totals } = useMemo(() => {
    const books = new Set()
    const lgs = new Set()
    const m = {}

    matrixData.forEach(row => {
      books.add(row.bookmaker)
      lgs.add(row.league)
      const key = `${row.bookmaker}|${row.league}`
      m[key] = { odds: row.odds_count, games: row.game_count }
    })

    const sortedBooks = Array.from(books).sort()
    const sortedLeagues = Array.from(lgs).sort((a, b) => {
      const ia = LEAGUE_ORDER.indexOf(a)
      const ib = LEAGUE_ORDER.indexOf(b)
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib)
    })

    // Calculate totals
    const bookTotals = {}
    const leagueTotals = {}
    let grandTotal = 0

    sortedBooks.forEach(book => {
      bookTotals[book] = { odds: 0, games: 0 }
      sortedLeagues.forEach(lg => {
        const d = m[`${book}|${lg}`] || { odds: 0, games: 0 }
        bookTotals[book].odds += d.odds
        bookTotals[book].games += d.games
        if (!leagueTotals[lg]) leagueTotals[lg] = { odds: 0, games: 0 }
        leagueTotals[lg].odds += d.odds
        leagueTotals[lg].games += d.games
        grandTotal += d.odds
      })
    })

    return { bookmakers: sortedBooks, leagues: sortedLeagues, matrix: m, totals: { books: bookTotals, leagues: leagueTotals, grand: grandTotal } }
  }, [matrixData])

  // Color intensity based on count
  function cellBg(count, maxCount) {
    if (!count) return 'transparent'
    const intensity = Math.min(count / (maxCount || 1), 1)
    return `rgba(0, 184, 212, ${0.05 + intensity * 0.2})`
  }

  const maxVal = Math.max(...matrixData.map(r => viewMode === 'odds' ? r.odds_count : r.game_count), 1)

  return (
    <div className="widget bmw-widget">
      <div className="widget-header">
        <h3 className="widget-title">
          <Grid3X3 size={16} />
          Book Matrix
          <span className="bmw-total">{totals.grand.toLocaleString()} total odds</span>
        </h3>
        <div className="bmw-header-actions">
          <div className="bmw-view-toggle">
            <button
              className={`bmw-view-btn ${viewMode === 'odds' ? 'active' : ''}`}
              onClick={() => setViewMode('odds')}
            >Odds</button>
            <button
              className={`bmw-view-btn ${viewMode === 'games' ? 'active' : ''}`}
              onClick={() => setViewMode('games')}
            >Games</button>
          </div>
          <button className="widget-refresh-btn" onClick={fetchMatrix} title="Refresh">
            <RefreshCw size={14} className={loading ? 'bmw-spinner' : ''} />
          </button>
        </div>
      </div>

      {error && <div className="bmw-error">{error}</div>}

      {loading && matrixData.length === 0 ? (
        <div className="bmw-loading">Loading book matrix...</div>
      ) : bookmakers.length === 0 ? (
        <div className="bmw-empty">No data available — scrapers may be offline</div>
      ) : (
        <div className="bmw-table-wrap">
          <table className="bmw-table">
            <thead>
              <tr>
                <th className="bmw-th bmw-th-book">Bookmaker</th>
                {leagues.map(lg => (
                  <th key={lg} className="bmw-th bmw-th-league">
                    <span className="bmw-league-dot" style={{ background: LEAGUE_COLORS[lg] || '#555' }} />
                    {lg}
                  </th>
                ))}
                <th className="bmw-th bmw-th-total">Total</th>
              </tr>
            </thead>
            <tbody>
              {bookmakers.map(book => (
                <tr key={book} className="bmw-row">
                  <td className="bmw-td bmw-td-book">
                    {getBookLogo(book) && <img src={getBookLogo(book)} alt="" className="bmw-book-logo" onError={e => { e.target.style.display = 'none' }} />}
                    {book}
                  </td>
                  {leagues.map(lg => {
                    const d = matrix[`${book}|${lg}`]
                    const val = d ? (viewMode === 'odds' ? d.odds : d.games) : 0
                    return (
                      <td
                        key={lg}
                        className={`bmw-td bmw-td-cell ${val ? '' : 'empty'}`}
                        style={{ background: cellBg(val, maxVal) }}
                      >
                        {val || '—'}
                      </td>
                    )
                  })}
                  <td className="bmw-td bmw-td-total">
                    {viewMode === 'odds'
                      ? (totals.books[book]?.odds || 0).toLocaleString()
                      : totals.books[book]?.games || 0}
                  </td>
                </tr>
              ))}
              {/* Footer totals */}
              <tr className="bmw-row bmw-footer-row">
                <td className="bmw-td bmw-td-book bmw-footer-label">Total</td>
                {leagues.map(lg => (
                  <td key={lg} className="bmw-td bmw-td-cell bmw-footer-val">
                    {viewMode === 'odds'
                      ? (totals.leagues[lg]?.odds || 0).toLocaleString()
                      : totals.leagues[lg]?.games || 0}
                  </td>
                ))}
                <td className="bmw-td bmw-td-total bmw-footer-val">
                  {totals.grand.toLocaleString()}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
